import { useQuery } from '@tanstack/react-query';
import { formatPreviewTimestamp } from '@web-app/components/Chat/chatUtils';
import { SUPPORT_CONCIERGE_ROUTE } from '@web-app/components/consts';
import type { ShellContribution } from '@web-app/components/Shell/shell-contribution';
import { SupportUnreadIndicator } from '@web-app/components/SupportConcierge/SupportUnreadIndicator';
import {
  mergeUnread,
  type ObservedConversations,
  observedKey,
} from '@web-app/hooks/support-concierge/support-observed';
import { useSupportAvailability } from '@web-app/hooks/support-concierge/useSupportAvailability';
import { supportQueryOptions } from '@web-app/hooks/support-concierge/useSupportConversations';
import { useSupportNewMessageToast } from '@web-app/hooks/support-concierge/useSupportNewMessageToast';
import { useIsPrescriber } from '@web-app/hooks/useIsPrescriber';
import { useNow } from '@web-app/hooks/utils/useNow';
import { useSupportConciergeAlertsEnabled } from '@web-app/utils/feature-flags/support-concierge';
import {
  conversationDirection,
  conversationStatus,
  plural,
  replySummary,
  sortByAttention,
  statusBadge,
} from '@web-app/utils/support-concierge/conversation-view';
import { trpc } from '@web-app/utils/trpc';
import { useRouter } from 'next/router';
import { LuMail } from 'react-icons/lu';

/**
 * Support Concierge in the shared shell: nav item, pinned bell section and
 * banner, all from one unread list so they never disagree.
 */
export function useSupportShellContribution(): ShellContribution {
  const { isPrescriber } = useIsPrescriber();
  const { viewerId, enabled } = useSupportAvailability(isPrescriber);
  const alertsEnabled = useSupportConciergeAlertsEnabled();
  const router = useRouter();
  const now = useNow();
  const unread = trpc.supportConcierge.unreadConversations.useQuery(
    { viewerId },
    { ...supportQueryOptions, enabled },
  );
  const { data: observed } = useQuery<ObservedConversations>({
    queryKey: observedKey(viewerId),
    queryFn: () => ({}),
    initialData: {},
    enabled: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  const conversations =
    enabled && !unread.data?.error
      ? sortByAttention(
          mergeUnread(
            unread.data?.conversations ?? [],
            unread.dataUpdatedAt,
            observed,
          ),
          now,
        )
      : [];
  const count = conversations.length;
  // Keeps the pilot override so a link never lands on "not available".
  const pilotQuery: Record<string, string> =
    router.query.allowConcierge === 'true' ? { allowConcierge: 'true' } : {};
  const openConcierge = (conversationId?: string) => {
    const query = new URLSearchParams({
      ...(conversationId ? { conversation: conversationId } : {}),
      ...pilotQuery,
    }).toString();
    void router.push(
      query ? `${SUPPORT_CONCIERGE_ROUTE}?${query}` : SUPPORT_CONCIERGE_ROUTE,
    );
  };
  const onConciergePage = router.pathname.startsWith(SUPPORT_CONCIERGE_ROUTE);
  // The page shows arrivals itself; elsewhere they pop up.
  useSupportNewMessageToast({
    viewerId,
    conversations,
    loaded: enabled && unread.isSuccess && !unread.data?.error,
    muted: onConciergePage,
    onView: openConcierge,
  });

  if (!enabled) return {};
  const nav = {
    path: SUPPORT_CONCIERGE_ROUTE,
    count,
    testId: 'support-unread-count',
    iconIndicator:
      count > 0 ? (
        <SupportUnreadIndicator className="ring-2 ring-sidebar" />
      ) : undefined,
    query: pilotQuery,
  };
  if (count === 0) return { nav };

  const { overdue } = replySummary(conversations, now);
  const items = conversations.map((conversation) => {
    const status = conversationStatus(conversation, now);
    const lastFromSupport = conversation.messages.findLast(
      (message) => !message.fromProvider,
    );
    return {
      id: conversation.id,
      title: conversation.title,
      meta: [
        `${conversationDirection(conversation) === 'incoming' ? 'New message' : 'New reply'} from Berry Street Support`,
        lastFromSupport &&
          formatPreviewTimestamp(new Date(lastFromSupport.createdAt * 1000)),
        status.kind === 'needs_reply' && statusBadge(status).label,
      ]
        .filter(Boolean)
        .join(' · '),
      onClick: () => openConcierge(conversation.id),
    };
  });

  const banner = {
    id: 'support-concierge-unread',
    icon: <LuMail />,
    title: `You have ${plural(count, 'unread message')} from Berry Street Support`,
    description: `${overdue > 0 ? `${overdue} overdue · ` : ''}Please read and reply within 24 hours to ensure prompt patient care.`,
    action: { label: 'Review Messages', onClick: () => openConcierge() },
  };

  return {
    nav,
    bell: {
      title: 'Support Concierge',
      icon: <LuMail aria-hidden="true" className="size-3.5" />,
      countLabel: plural(count, 'unread message'),
      items,
      footer: {
        label: 'View All Support Messages',
        onClick: () => openConcierge(),
      },
    },
    ...(alertsEnabled &&
      !onConciergePage && {
        banner,
        reminder: {
          ...banner,
          description:
            'Messages from the support team need a response within 24 hours to keep patient care on track.',
          items,
          // Conversations are sorted most urgent first.
          action: {
            label: 'Read Messages',
            onClick: () => openConcierge(conversations[0]?.id),
          },
        },
      }),
  };
}
